import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import researchReportJobsApi from "../apis/researchReportJobsApi";
import { getApiErrorMessage } from "../utils/apiErrorMessage";
import { bindResearchReportPrintHandlers } from "../utils/researchReportPrint";

/**
 * Technician report viewer route:
 * - enforces server-side MSP/job authorization
 * - renders returned HTML artifact inside sandboxed iframe
 * - supports print/PDF and Excel download actions
 */
const ResearchReportView: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportHtml, setReportHtml] = useState<string>("");
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const printCleanupRef = useRef<(() => void) | null>(null);

  const normalizedParams = useMemo(
    () => ({
      jobId: String(jobId || "").trim(),
    }),
    [jobId]
  );

  const reportTitle = useMemo(() => {
    const match = reportHtml.match(/<h1[^>]*>([^<]+)<\/h1>/i);
    return match?.[1]?.trim() || "";
  }, [reportHtml]);

  useEffect(() => {
    document.title = "Deep Research Report - Theo";
    return () => {
      document.title = "Theo";
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!/^\d+$/.test(normalizedParams.jobId)) {
        setLoading(false);
        setError("Invalid report URL.");
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const html = await researchReportJobsApi.getReportHtml({ jobId: normalizedParams.jobId });
        setReportHtml(String(html || ""));
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to load report."));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [normalizedParams]);

  useEffect(() => {
    return () => {
      printCleanupRef.current?.();
      printCleanupRef.current = null;
    };
  }, []);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    const win = iframe?.contentWindow;
    printCleanupRef.current?.();
    printCleanupRef.current = bindResearchReportPrintHandlers(doc, win);
  };

  const handlePrintReport = () => {
    const printWindow = iframeRef.current?.contentWindow;
    if (!printWindow) return;
    printWindow.focus();
    printWindow.print();
  };

  const handleDownloadXlsx = async () => {
    if (!normalizedParams.jobId) return;
    try {
      setDownloadingXlsx(true);
      setDownloadError(null);
      const blob = await researchReportJobsApi.downloadReportXlsx({
        jobId: normalizedParams.jobId,
        title: reportTitle,
      });
      const safeTitle = (reportTitle || "deep-research-report")
        .replace(/[^\w.-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${safeTitle || "deep-research-report"}-${normalizedParams.jobId}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(getApiErrorMessage(err, "Failed to download Excel report."));
    } finally {
      setDownloadingXlsx(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100%", display: "grid", placeItems: "center", p: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading report...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: "calc(100vh - 96px)", bgcolor: "#fff" }}>
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid #E5E7EB",
          bgcolor: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        {downloadError ? (
          <Typography variant="caption" color="error" sx={{ mr: 1 }}>
            {downloadError}
          </Typography>
        ) : null}
        <Tooltip title="Download Excel report">
          <span>
            <IconButton
              aria-label="Download Excel report"
              onClick={() => {
                void handleDownloadXlsx();
              }}
              disabled={!reportHtml || downloadingXlsx}
              size="small"
              sx={{
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#F9FAFB" },
              }}
            >
              {downloadingXlsx ? <CircularProgress size={16} /> : <FileDownloadIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Print report (landscape PDF)">
          <span>
            <IconButton
              aria-label="Print report"
              onClick={handlePrintReport}
              disabled={!reportHtml}
              size="small"
              sx={{
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#F9FAFB" },
              }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <iframe
        ref={iframeRef}
        title="Deep Research Report"
        srcDoc={reportHtml}
        onLoad={handleIframeLoad}
        style={{ width: "100%", height: "100%", minHeight: "calc(100vh - 152px)", border: 0 }}
        sandbox="allow-same-origin allow-scripts allow-modals"
      />
    </Box>
  );
};

export default ResearchReportView;
